import {
  Button,
  Checkbox,
  Container,
  Form,
  Input,
  Typography,
} from "@ster/ster-toolkit";
import { Alert, Form as AntForm, Row } from "antd";
import { Store } from "antd/lib/form/interface";
import { Suspense, memo, useCallback, useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import { ProposalRequestLite } from "../../api";
import { Result, proposalRequestState, proposalState } from "../../atoms";
import { Order } from "../../types";
import SimpleSpinner from "../SimpleSpinner";
import styles from "./ContactForm.module.less";

const SubmitButton = ({
  order,
  onReady,
  disabled,
}: {
  order: Order;
  onReady: (r: Result) => void;
  disabled: boolean;
}) => {
  const [_, setProposal] = useRecoilState(proposalState);
  const result = useRecoilValue(proposalRequestState);
  useEffect(() => {
    if (!disabled && result) {
      onReady(result);
      setProposal(null);
    }
  }, [disabled, onReady, result, setProposal]);

  return (
    <Button
      mode="tertiary"
      htmlType="submit"
      disabled={
        disabled ||
        order.subOrders.filter((s) => s.budget && s.budget > 0).length === 0
      }
      id="requestProposal"
    >
      Verstuur aanvraag
    </Button>
  );
};

export type ContactFormState = "initial" | "success" | "error";

const ContactForm = ({ order }: { order: Order }): JSX.Element => {
  const [form] = AntForm.useForm<ProposalRequestLite>();
  const [proposal, setProposal] = useRecoilState(proposalState);
  const handleFormSubmit = useCallback(
    (values: Store) => {
      const proposalData: ProposalRequestLite = {
        medium: order.medium,
        userEmail: values.userEmail,
        userFullName: values.userFullName,
        userCompany: values.userCompany,
        starterDocumentRequest: values.starterDocumentRequest ?? false,
        referer: window.location.href,
        subOrders: [
          ...order.subOrders.map(
            ({ period, calculated, packageCode, ...rest }) => ({
              ...rest,
              from: period[0],
              to: period[1],
              grp: calculated?.grp,
              spotsPerDayChannel: calculated?.spotsPerDayChannel,
              spotsTotal: calculated?.spots,
              packageCode: packageCode ?? "",
            }),
          ),
        ],
        forecastConversionGroups: order.forecastConversionGroups,
      };

      setProposal(proposalData);
    },
    [
      order.forecastConversionGroups,
      order.medium,
      order.subOrders,
      setProposal,
    ],
  );

  const [formState, setFormState] = useState<ContactFormState>("initial");
  const handleReady = useCallback((r: Result) => {
    switch (r) {
      case "success":
        setFormState("success");
        break;
      case "error":
        setFormState("error");
        break;
      default:
        setFormState("initial");
        break;
    }
  }, []);

  const handleValuesChange = useCallback(() => {
    setFormState("initial");
  }, []);

  return (
    <Container className={styles.contactForm}>
      <Form
        form={form}
        initialValues={proposal ?? {}}
        layout="horizontal"
        onFinish={handleFormSubmit}
        onValuesChange={handleValuesChange}
      >
        <Typography.Title level={2}>
          Ontvang een uitgebreid campagnevoorstel in je mailbox
        </Typography.Title>
        <Typography.Paragraph className={styles.content}>
          Dankzij de Ster Campagnecalculator heb je nu inzicht in het verwachte
          campagneresultaat binnen de door jou gekozen doelgroep, medium en
          periode. Dit hebben we voor je uitgewerkt in een uitgebreid
          campagnevoorstel, inclusief aanvullende informatie over Ster, de
          zenders van de publieke omroep en onze inkoopmogelijkheden. Laat je
          e-mailadres achter en ontvang het campagnevoorstel direct.
        </Typography.Paragraph>
        <Form.Item
          label="Naam"
          name="userFullName"
          rules={[
            {
              required: true,
              message: "Vul je naam in",
            },
          ]}
        >
          <Input className={styles.contactInput} />
        </Form.Item>
        <Form.Item
          label="Bedrijfsnaam"
          name="userCompany"
          rules={[
            {
              required: true,
              message: "Vul je bedrijfsnaam in",
            },
          ]}
        >
          <Input className={styles.contactInput} />
        </Form.Item>
        <Form.Item
          label="E-mailadres"
          name="userEmail"
          rules={[
            {
              required: true,
              message: "Vul je e-mailadres in",
              type: "email",
            },
          ]}
        >
          <Input className={styles.contactInput} />
        </Form.Item>
        <Form.Item
          label=" "
          name="starterDocumentRequest"
          valuePropName="checked"
        >
          <Checkbox>
            Stuur mij ook het stappenplan &apos;Starten met reclame&apos; toe.
          </Checkbox>
        </Form.Item>
        {formState !== "initial" && (
          <Alert
            showIcon
            type={formState}
            message=""
            description={
              formState === "success" ? (
                <>
                  Bedankt voor je aanvraag, je ontvangt je campagne binnen
                  enkele momenten in je mailbox.
                  <br />
                  <em>
                    Meer leren over landelijke reclame en wat de mogelijkheden
                    zijn? Meld je nu aan voor onze{" "}
                    <a
                      href="https://ster.nl/ster-workshop-reclamestarters/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      workshop voor reclamestarters
                    </a>
                    .
                  </em>
                </>
              ) : (
                "Er is iets mis gegaan met je aanvraag, probeer het opnieuw of neem contact op met Ster."
              )
            }
          />
        )}
        <Row justify="end">
          <Suspense fallback={<SimpleSpinner />}>
            <Form.Item shouldUpdate noStyle>
              {() => (
                <SubmitButton
                  order={order}
                  onReady={handleReady}
                  disabled={
                    formState === "success" ||
                    !form.isFieldsTouched(
                      ["userFullName", "userCompany", "userEmail"],
                      true,
                    ) ||
                    !!form
                      .getFieldsError()
                      .filter(({ errors }) => errors.length).length
                  }
                />
              )}
            </Form.Item>
          </Suspense>
        </Row>
      </Form>
    </Container>
  );
};

export default memo(ContactForm);
